import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack
} from '@mui/material'
import { Pool as PoolIcon, Add as AddIcon } from '@mui/icons-material'
import Layout from '../components/Layout/Layout'
import { usePool } from '../contexts/PoolContext'
import { useMeasurements } from '../hooks/useMeasurements'
import MeasurementForm from '../components/Measurements/MeasurementForm'
import PoolSelector from '../components/Pool/PoolSelector'
import usePageTracking from '../hooks/usePageTracking'

const AddMeasurement = () => {
  usePageTracking('Add Measurement');
  
  const navigate = useNavigate()
  const { pools, selectedPool, setSelectedPool } = usePool()
  const [currentPool, setCurrentPool] = useState(selectedPool)
  const { createMeasurement, getLatestMeasurement } = useMeasurements(currentPool?.id)
  const [showForm, setShowForm] = useState(false)

  const latestMeasurement = getLatestMeasurement()

  // Auto-select single pool or use pre-selected pool
  useEffect(() => {
    if (pools.length === 1) {
      setCurrentPool(pools[0])
      setShowForm(true)
    } else if (selectedPool) {
      setCurrentPool(selectedPool)
      setShowForm(true)
    }
  }, [pools, selectedPool])

  const handlePoolChange = (pool) => {
    setCurrentPool(pool)
    setSelectedPool(pool) // Update global selection
    setShowForm(true)
  }

  const handleSaveMeasurement = async (measurementData) => {
    const result = await createMeasurement(measurementData)
    if (!result.error) {
      navigate('/dashboard')
    }
    return result
  }

  const handleCancel = () => {
    navigate('/dashboard')
  }

  const handleCreatePool = () => {
    navigate('/pools')
  }

  // Show pool selection if no pools exist
  if (pools.length === 0) {
    return (
      <Layout>
        <Box sx={{ textAlign: 'center', py: { xs: 6, md: 12 } }}>
          <PoolIcon
            sx={{
              fontSize: { xs: 48, md: 64 },
              color: 'text.disabled',
              mb: 2
            }}
          />
          <Typography variant="h5" component="h2" gutterBottom>
            No Pools Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            You need to create a pool before adding measurements.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreatePool}
            size="large"
            sx={{ 
              borderRadius: 2,
              py: 1.5,
              px: 4,
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            Create Your First Pool
          </Button>
        </Box>
      </Layout>
    )
  }

  return (
    <Layout>
      <Box sx={{ maxWidth: 'md', mx: 'auto', p: 2 }}>
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            borderRadius: 3,
            mb: 3
          }}
        >
          <Stack spacing={3}>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                Add New Measurement
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Record your pool's chemistry levels
              </Typography>
            </Box>

            <PoolSelector
              selectedPool={currentPool}
              onPoolChange={handlePoolChange}
              label="Pool"
              showAlert={true}
            />
          </Stack>
        </Paper>

        {showForm && currentPool && (
          <MeasurementForm
            defaultValues={latestMeasurement}
            onSave={handleSaveMeasurement}
            onCancel={handleCancel}
          />
        )}
      </Box>
    </Layout>
  )
}

export default AddMeasurement